/**
 * Utility functions for detecting duplicate papers
 */

import { supabase } from '@/integrations/supabase/client';
import { extractArxivId } from './arxiv-utils';

/**
 * Normalizes an arXiv ID by removing version indicators
 * Example: "2301.12345v1" -> "2301.12345"
 */
export function normalizeArxivId(arxivId: string): string {
  return arxivId.replace(/v\d+$/, '');
}

/**
 * Extracts and normalizes arXiv ID from URL
 * Example: "https://arxiv.org/abs/2301.12345v2" -> "2301.12345"
 */
export function extractNormalizedArxivId(url: string): string | null {
  const fullId = extractArxivId(url);
  if (!fullId) return null;
  return normalizeArxivId(fullId);
}

/**
 * Checks if a paper with the same arXiv ID already exists on the platform
 */
export async function checkForDuplicateArxivPaper(arxivUrl: string): Promise<{
  isDuplicate: boolean;
  existingPaper?: {
    id: string;
    title: string;
    external_url: string;
  };
  error?: string;
}> {
  try {
    const normalizedId = extractNormalizedArxivId(arxivUrl);
    
    if (!normalizedId) {
      return { isDuplicate: false };
    }

    // Query papers where external_url contains the normalized arXiv ID
    const { data: papers, error } = await supabase
      .from('papers')
      .select('id, title, external_url')
      .not('external_url', 'is', null)
      .ilike('external_url', `%${normalizedId}%`);

    if (error) {
      console.error('Error checking for duplicate papers:', error);
      return { 
        isDuplicate: false, 
        error: 'Failed to check for duplicates' 
      };
    }

    // Filter results to ensure we match the exact normalized arXiv ID
    const duplicates = papers?.filter(paper => {
      if (!paper.external_url) return false;
      const existingId = extractNormalizedArxivId(paper.external_url);
      return existingId === normalizedId;
    }) || [];

    if (duplicates.length > 0) {
      return {
        isDuplicate: true,
        existingPaper: duplicates[0]
      };
    }

    return { isDuplicate: false };
  } catch (error) {
    console.error('Error in duplicate detection:', error);
    return { 
      isDuplicate: false, 
      error: 'Failed to check for duplicates' 
    };
  }
}